/** @jsx jsx */
import { jsx } from "theme-ui"
import PropTypes from "prop-types"

import Link from "./link"
import Button from "./button"

import { ab } from "../theme"
import { useCookiebotConsent } from "../hooks/use-cookiebot-consent"

/**
 * Gates third-party content behind Cookiebot consent for a single category.
 * Renders its children once the visitor has consented to `category`
 * (`necessary` | `preferences` | `statistics` | `marketing`); otherwise it
 * shows a placeholder whose button reopens the Cookiebot dialog via
 * `Cookiebot.renew()`.
 *
 * Only `category="none"` (e.g. a locally hosted asset that sets no
 * third-party cookies) renders the children ungated. Any unset or
 * unrecognised category **fails closed**: it is gated as if it required the
 * strictest (`marketing`) consent, so a forgotten external embed never leaks
 * before consent.
 *
 * Replaces the legacy `BlockThirdParty` component, whose consent lived in a
 * local `third-party-permission` cookie disconnected from Cookiebot.
 */
const KNOWN_CATEGORIES = [
  "necessary",
  "preferences",
  "statistics",
  "marketing",
]
const ConsentGate = ({
  children,
  category,
  backgroundUrl,
  buttonText,
  infoText,
  infoUrl,
  infoUrlText,
}) => {
  // Fail closed: anything that is not an explicit "none" and not a recognised
  // category is gated against the strictest consent so it cannot leak.
  const resolvedCategory = KNOWN_CATEGORIES.includes(category)
    ? category
    : "marketing"
  const consented = useCookiebotConsent(resolvedCategory)

  if (category === "none" || consented) {
    return <div>{children}</div>
  }

  const renew = () => window.Cookiebot?.renew()

  return (
    <div
      className="consent-gate-container"
      sx={{
        background: `linear-gradient(rgba(0,0,0,.15), rgba(0,0,0,.15)), url(${backgroundUrl})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        color: "#fff",
        py: 5,
        px: 3,
        variant: ab({ md: "spacingContainer" }),
      }}
    >
      <div
        className="info"
        sx={{
          textAlign: "center",
        }}
      >
        <p sx={{ fontSize: 3 }}>
          <strong>{infoText}</strong>
        </p>
        {infoUrl && (
          <Link to={infoUrl} sx={{ color: "#fff" }} target="_blank">
            {infoUrlText}
          </Link>
        )}
      </div>
      <div
        className="actions"
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 3,
        }}
      >
        <div className="allow-consent" sx={{ padding: 2 }}>
          <Button
            buttonText={buttonText}
            onClick={renew}
            style={{ color: "#fff", borderColor: "#fff" }}
          />
        </div>
      </div>
    </div>
  )
}

ConsentGate.propTypes = {
  children: PropTypes.node,
  category: PropTypes.oneOf([
    "none",
    "necessary",
    "preferences",
    "statistics",
    "marketing",
  ]),
  backgroundUrl: PropTypes.string,
  buttonText: PropTypes.string.isRequired,
  infoText: PropTypes.string.isRequired,
  infoUrl: PropTypes.string,
  infoUrlText: PropTypes.string,
}

export default ConsentGate
